import React, { useState, useEffect } from 'react';
import { Box } from '@mui/material';
import useSnackbar from '../../../hooks/useSnackbar';
import { DateField } from '../../../pages/Booking/Details';
import { PrimaryButton, SecondaryButton } from '../../atoms/Button';

export interface DateFilterData {
  startDate: string | null;
  endDate: string | null;
  filterType: string;
}

const DateFilterComp = ({
  onFiltered,
  onResetFilter,
  handleDrawerClose,
  tabValue,
  startDate,
  endDate,
}: any) => {
  const { showSnackbar, SnackbarComponent } = useSnackbar();

  const formatDate = (date: Date) => {
    const d = new Date(date.getFullYear(), date.getMonth(), date.getDate());
    return d.toISOString().split('T')[0];
  };

  const todayDate = new Date();
  const today = formatDate(todayDate);

  const yesterdayDate = new Date();
  yesterdayDate.setDate(yesterdayDate.getDate() - 1);
  const previousDate = formatDate(yesterdayDate);

  const [filterData, setFilterData] = useState<DateFilterData>({
    startDate: startDate || null,
    endDate: endDate || null,
    filterType: tabValue,
  });

  useEffect(() => {
    setFilterData({
      startDate: startDate || null,
      endDate: endDate || null,
      filterType: tabValue,
    });
  }, [startDate, endDate, tabValue]);

  useEffect(() => {
    if (tabValue === 'TODAY') {
      setFilterData({
        startDate: today,
        endDate: today,
        filterType: 'TODAY',
      });
    } else if (tabValue === 'OPEN_CALLS') {
      setFilterData({
        startDate: previousDate,
        endDate: previousDate,
        filterType: 'OPEN_CALLS',
      });
    }
  }, [tabValue]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;

    setFilterData((prev) => {
      const updated = { ...prev, [name]: value };

      if (
        updated.startDate &&
        updated.endDate &&
        new Date(updated.startDate) > new Date(updated.endDate)
      ) {
        if (name === 'startDate') {
          updated.endDate = value;
        }
        if (name === 'endDate') {
          return prev;
        }
      }

      return updated;
    });
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    if (filterData.filterType === 'TODAY') return;

    if (filterData.startDate && new Date(filterData.startDate) > new Date()) {
      showSnackbar('Start date cannot be in the future', 'warning');
      return;
    }

    if (
      filterData.startDate &&
      filterData.endDate &&
      new Date(filterData.startDate) > new Date(filterData.endDate)
    ) {
      showSnackbar('Start date cannot be greater than end date', 'warning');
      return;
    }

    if (
      tabValue === 'OPEN_CALLS' &&
      filterData.endDate &&
      new Date(filterData.endDate) >= todayDate
    ) {
      showSnackbar(
        'End date cannot be today or later for OPEN_CALLS',
        'warning',
      );
      return;
    }

    onFiltered(filterData);
    handleDrawerClose();
  };

  const handleReset = () => {
    setFilterData({
      startDate: null,
      endDate: null,
      filterType: 'ALL',
    });
    onResetFilter();
  };

  return (
    <Box sx={{ flex: 1 }}>
      {SnackbarComponent}

      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
        <DateField
          name="startDate"
          label="Start Date"
          type="date"
          value={filterData.startDate || ''}
          onChange={handleChange}
          InputLabelProps={{ shrink: true }}
          fullWidth
          inputProps={{
            max: today,
          }}
        />

        <DateField
          name="endDate"
          label="End Date"
          type="date"
          value={filterData.endDate || ''}
          onChange={handleChange}
          InputLabelProps={{ shrink: true }}
          fullWidth
          inputProps={{
            min: filterData.startDate || undefined,
            max: tabValue === 'OPEN_CALLS' ? previousDate : today,
          }}
        />

        <Box sx={{ display: 'flex', gap: 2, mt: 3 }}>
          <SecondaryButton title="Reset" onClick={handleReset} />
          <PrimaryButton title="Apply Filter" onClick={handleSubmit} />
        </Box>
      </Box>
    </Box>
  );
};

export default DateFilterComp;
